import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setStep, setErrors, clearErrors, populateForEdit, resetForm, updateField } from "../../reduxSlice/profileSlice";
import { postApiWithAuth, postApiWithAuthFile } from "../../services/api/api";
import "./CreateProfile.css";

import { useGetMastersQuery } from "../../services/userAuthApi";

import Step1BasicInfo, { validateStep1 } from "./components/Step1BasicInfo";
import Step2PersonalDetails, { validateStep2 } from "./components/Step2PersonalDetails";
import Step3FamilyBackground, { validateStep3 } from "./components/Step3FamilyBackground";
import Step4EducationProfession, { validateStep4 } from "./components/Step4EducationProfession";
import Step5ProfileMedia, { validateStep5 } from "./components/Step5ProfileMedia";
import Step6PreferencesContact, { validateStep6 } from "./components/Step6PreferencesContact";

const steps = [
    { id: 1, title: "Basic Information", sub: "Enter your basic personal details" },
    { id: 2, title: "Personal & Physical Details", sub: "Share your physical information" },
    { id: 3, title: "Family & Background", sub: "Provide your family background details" },
    { id: 4, title: "Education & Profession", sub: "Add education and career information" },
    { id: 5, title: "Profile & Media", sub: "Upload your photos and videos" },
    { id: 6, title: "Preferences Expectations", sub: "Provide your matching details" },
];

const CreateProfile = ({ mode = "create" }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [profileId, setProfileId] = useState(null);

    // Captured processing toggle state linked from Step 5 face tracking status
    const [isMediaProcessing, setIsMediaProcessing] = useState(false);

    const { data: responseMasters, isLoading: mastersLoading } = useGetMastersQuery();
    const masters = responseMasters?.data || {};

    const token = localStorage.getItem("token");
    const isAuthenticated = !!token;

    const currentStep = useSelector((state) => state.profile.currentStep);
    const globalFormData = useSelector((state) => state.profile);
    const isEditMode = useSelector((state) => state.profile.isEditMode);

    useEffect(() => {
        if (!isAuthenticated) return;

        const storedProfileStr = localStorage.getItem("profile_data");
        let storedProfile = null;

        try {
            if (storedProfileStr) {
                const parsedArray = JSON.parse(storedProfileStr);
                storedProfile = Array.isArray(parsedArray) ? parsedArray[0] : parsedArray;
            }
        } catch (e) {
            console.error("Error parsing stored profile_data array from login session:", e);
        }

        if (mode === "edit" || storedProfile) {
            const fetchProfileData = async () => {
                setLoadingProfile(true);
                try {
                    const targetProfileId = storedProfile?.id;
                    setProfileId(targetProfileId);

                    const response = await postApiWithAuth("/get-profile", { profile_id: targetProfileId });
                    const profileData = response?.data;

                    if (profileData) {
                        let heightStr = String(profileData.height || "");
                        let [ft, inchRaw] = heightStr.split(".");

                        let normalizedInches = "0";
                        if (inchRaw) {
                            let subSegment = inchRaw.substring(0, 2);
                            let parsedVal = parseInt(subSegment, 10);

                            if (!isNaN(parsedVal) && parsedVal >= 0 && parsedVal <= 11) {
                                normalizedInches = String(parsedVal);
                            }
                        }
                        const mappedProfile = {
                            id: profileData.id,
                            firstName: profileData.first_name || "",
                            middleName: profileData.middle_name || "",
                            lastName: profileData.last_name || "",
                            email: profileData.email || "",
                            gender: profileData.gender || "",
                            maritalStatus: profileData.marital_status || "",
                            birthPlace: profileData.birth_place || "",
                            birthDate: profileData.brithday || "",
                            birthTime: profileData.birth_time ? (() => {
                                const timeStr = String(profileData.birth_time);
                                const parts = timeStr.split(":");
                                if (parts.length >= 2) {
                                    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
                                }
                                return timeStr.substring(0, 5);
                            })() : "",
                            heightFeet: ft || "",
                            heightInches: normalizedInches,
                            weight: profileData.weight ? String(profileData.weight) : "",
                            complexion: profileData.complexion || "",
                            bodyBuild: profileData.body_build || "",
                            bloodGroup: profileData.blood_group || "",
                            petName: profileData.petname || "",
                            maternalSurname: profileData.maternal_surname || "",
                            nativePlace: profileData.native_place || "",
                            gotra: profileData.gotra || "",
                            brothersMarried: (profileData.brothers_married !== undefined && profileData.brothers_married !== null) ? String(profileData.brothers_married) : "0",
                            brothersUnmarried: (profileData.brothers_unmarried !== undefined && profileData.brothers_unmarried !== null) ? String(profileData.brothers_unmarried) : "0",
                            sistersMarried: (profileData.sisters_married !== undefined && profileData.sisters_married !== null) ? String(profileData.sisters_married) : "0",
                            sistersUnmarried: (profileData.sisters_unmarried !== undefined && profileData.sisters_unmarried !== null) ? String(profileData.sisters_unmarried) : "0",
                            highestEducation: profileData.heighest_education || "",
                            highestEducationOther: profileData.heighest_education_other || "",
                            secondHighestEducation: profileData.second_heighest_education || "",
                            secondHighestEducationOther: profileData.second_heighest_education_other || "",
                            specialization: profileData.education_stream || "",
                            jobType: profileData.job_type || "",
                            organizationName: profileData.organization_name || "",
                            occupation: profileData.occupation || "",
                            designation: profileData.designation || "",
                            income: profileData.income ? String(profileData.income) : "",
                            workLocation: profileData.location || "",
                            mainPhoto: profileData.main_photo || "",
                            galleryPhotos: Array.isArray(profileData.profile_photos)
                                ? profileData.profile_photos.map(item => ({
                                    id: item.id,
                                    url: item.photo
                                }))
                                : [],
                            //galleryPhotos: Array.isArray(profileData.photos) ? profileData.photos.map(url => ({ url })) : [],
                            videoFile: profileData.main_video || null,
                            expectations: profileData.partner_expectations || "",
                            expectationsDescription: profileData.partner_expectations_description || "",
                            matchHoroscope: Number(profileData.match_horoscope) === 1,
                            differentlyAbled: Number(profileData.is_handicapped) === 1,
                            disabilityDescription: profileData.disability_description || "",
                            address: profileData.new_location || "",
                            contactName: profileData.contact_name || "",
                            contactNumber: profileData.contact_number || "",
                            secContactName: profileData.sec_contact_name || "",
                            secContactNumber: profileData.sec_contact_number || "",
                            correspondenceAddress: profileData.correspondence_address || "",
                            facebook: profileData.social_facebook || "",
                            linkedin: profileData.social_linkedin || "",
                            instagram: profileData.social_instagram || "",
                            contactCountryCode: profileData.primary_country_code || "",
                            secContactCountryCode: profileData.sec_country_code || "",
                            primaryCountry: profileData.country || "",
                            primaryCountryId: profileData.country || "",

                        };

                        dispatch(populateForEdit(mappedProfile));
                    }
                } catch (error) {
                    console.error("API initialization error encountered loading profile settings:", error);
                    alert("Failed to retrieve profile configuration data from server.");
                } finally {
                    setLoadingProfile(false);
                }
            };

            fetchProfileData();
        } else {
            const savedDraft = localStorage.getItem("profile_creation_draft");
            const savedStep = localStorage.getItem("profile_creation_step");

            if (savedDraft) {
                try {
                    const parsedDraft = JSON.parse(savedDraft);
                    Object.keys(parsedDraft).forEach((key) => {
                        dispatch(updateField({ field: key, value: parsedDraft[key] }));
                    });
                    if (savedStep) {
                        dispatch(setStep(Number(savedStep)));
                    }
                } catch (error) {
                    console.error("Draft hydration encountered an issue, defaulting clean window:", error);
                    dispatch(resetForm());
                }
            } else {
                dispatch(resetForm());
            }
        }
    }, [mode, dispatch, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || isEditMode) return;

        const { currentStep: _, errors: __, isEditMode: ___, ...fieldsToSave } = globalFormData;

        const hasActiveData = Object.values(fieldsToSave).some(
            val => val !== "" && val !== null && val !== undefined && val !== false && (Array.isArray(val) ? val.length > 0 : true)
        );

        if (hasActiveData) {
            localStorage.setItem("profile_creation_draft", JSON.stringify(fieldsToSave));
            localStorage.setItem("profile_creation_step", String(currentStep));
        }
    }, [globalFormData, isAuthenticated, isEditMode, currentStep]);

    useEffect(() => {
        if (!isSubmitted) return;

        const redirectTimer = setTimeout(() => {
            navigate("/dashboard");
        }, 3000);

        return () => clearTimeout(redirectTimer);
    }, [isSubmitted, navigate]);

    if (!isAuthenticated) {
        return (
            <div className="container mt-5 text-center">
                <div className="card p-5 shadow-sm mx-auto" style={{ maxWidth: "500px", borderRadius: "12px" }}>
                    <div className="text-danger mb-3"><i className="fa fa-exclamation-triangle fa-3x" aria-hidden="true"></i></div>
                    <h3 className="fw-bold mb-2">Access Denied</h3>
                    <p className="text-muted mb-4">You must be logged in or registered to configure your matrimony profile settings.</p>
                    <button className="btn next-btn w-100" onClick={() => navigate("/")}>Go to Login / Register</button>
                </div>
            </div>
        );
    }

    if (mastersLoading || loadingProfile) {
        return (
            <div className="text-center my-5 p-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="text-muted mt-2">Loading platform configuration data...</p>
            </div>
        );
    }

    const runValidation = (step) => {
        let stepErrors = {};
        switch (step) {
            case 1: stepErrors = validateStep1(globalFormData); break;
            case 2: stepErrors = validateStep2(globalFormData); break;
            case 3: stepErrors = validateStep3(globalFormData); break;
            case 4: stepErrors = validateStep4(globalFormData); break;
            case 5: stepErrors = validateStep5(globalFormData); break;
            case 6: stepErrors = validateStep6(globalFormData); break;
            default: break;
        }
        dispatch(setErrors(stepErrors));
        return Object.keys(stepErrors).length === 0;
    };

    const getExtensionFromMime = (mimeType) => {
        switch (mimeType) {
            case "image/jpeg":
            case "image/jpg":
                return "jpg";
            case "image/png":
                return "png";
            case "video/mp4":
                return "mp4";
            case "video/quicktime":
                return "mov";
            case "video/x-msvideo":
                return "avi";
            case "video/x-matroska":
                return "mkv";
            default:
                return mimeType.split("/")[1] || "png";
        }
    };

    const nextStep = async () => {
        if (!runValidation(currentStep)) return;
        if (currentStep >= steps.length) return;

        setSubmitting(true);
        const stepPayload = new FormData();

        const userDetailsStr = localStorage.getItem("userDetails");
        let mobileNumber = "";
        let userId = "";
        if (userDetailsStr) {
            try {
                const details = JSON.parse(userDetailsStr);
                mobileNumber = details.mobile || "";
                userId = details.id || "";
            } catch (e) {
                console.error("Parsing stored structural details failed:", e);
            }
        }

        stepPayload.append("step", String(currentStep));
        if (profileId) {
            stepPayload.append("profile_id", String(profileId));
            stepPayload.append("id", String(profileId));
        }

        switch (currentStep) {
            case 1:
                stepPayload.append("user_created", userId);
                stepPayload.append("first_name", globalFormData.firstName || "");
                stepPayload.append("middle_name", globalFormData.middleName || "");
                stepPayload.append("last_name", globalFormData.lastName || "");
                stepPayload.append("email", globalFormData.email || "");
                stepPayload.append("gender", globalFormData.gender || "");
                stepPayload.append("mobile", mobileNumber);
                stepPayload.append("brithday", globalFormData.birthDate || "");
                stepPayload.append("birth_time", globalFormData.birthTime || "");
                break;

            case 2:
                stepPayload.append("marital_status", globalFormData.maritalStatus || "");
                stepPayload.append("birth_place", globalFormData.birthPlace || "");
                stepPayload.append("new_birth_place", globalFormData.birthPlace || "");
                if (globalFormData.heightFeet) {
                    const calculatedHeight = `${globalFormData.heightFeet}.${globalFormData.heightInches || 0}`;
                    stepPayload.append("height", calculatedHeight);
                }
                if (globalFormData.weight && String(globalFormData.weight).trim() !== "" && !isNaN(globalFormData.weight)) {
                    stepPayload.append("weight", Number(globalFormData.weight));
                }
                stepPayload.append("blood_group", globalFormData.bloodGroup || "");
                stepPayload.append("complexion", globalFormData.complexion || "");
                stepPayload.append("body_build", globalFormData.bodyBuild || "");
                stepPayload.append("disability_description", globalFormData.disabilityDescription || "");
                stepPayload.append("is_handicapped", globalFormData.differentlyAbled ? 1 : 0);
                break;

            case 3:
                stepPayload.append("petname", globalFormData.petName || "");
                stepPayload.append("pet_name", globalFormData.petName || "");
                stepPayload.append("maternal_surname", globalFormData.maternalSurname || "");
                stepPayload.append("native_place", globalFormData.nativePlace || "");
                stepPayload.append("new_native_place", globalFormData.nativePlace || "");
                stepPayload.append("gotra", globalFormData.gotra || "");
                stepPayload.append("brothers_married", (Number(globalFormData.brothersMarried) || 0));
                stepPayload.append("brothers_unmarried", (Number(globalFormData.brothersUnmarried) || 0));
                stepPayload.append("sisters_married", (Number(globalFormData.sistersMarried) || 0));
                stepPayload.append("sisters_unmarried", (Number(globalFormData.sistersUnmarried) || 0));
                break;

            case 4:
                stepPayload.append("education", globalFormData.highestEducation || "");
                stepPayload.append("heighest_education", globalFormData.highestEducation || "");
                stepPayload.append("heighest_education_other", globalFormData.highestEducationOther || "");
                stepPayload.append("second_heighest_education", globalFormData.secondHighestEducation || "");
                stepPayload.append("second_heighest_education_other", globalFormData.secondHighestEducationOther || "");
                stepPayload.append("education_stream", globalFormData.specialization || "");
                stepPayload.append("job_type", globalFormData.jobType || "");
                stepPayload.append("organization_name", globalFormData.organizationName || "");
                stepPayload.append("occupation", globalFormData.occupation || "");
                stepPayload.append("designation", globalFormData.designation || "");
                if (globalFormData.income && String(globalFormData.income).trim() !== "" && !isNaN(globalFormData.income)) {
                    stepPayload.append("income", Number(globalFormData.income));
                }
                stepPayload.append("income_currency", "INR");
                stepPayload.append("location", globalFormData.workLocation || "");
                //stepPayload.append("country", "India");
                break;

            case 5:
                // Helper utility to convert base64 strings back to Binary Blobs on the fly
                const dataURLtoBlob = (dataurl) => {
                    const arr = dataurl.split(',');
                    const mime = arr[0].match(/:(.*?);/)[1];
                    const bstr = atob(arr[1]);
                    let n = bstr.length;
                    const u8arr = new Uint8Array(n);
                    while (n--) {
                        u8arr[n] = bstr.charCodeAt(n);
                    }
                    return new Blob([u8arr], { type: mime });
                };

                // Helper utility to resolve file binaries whether they are local blob strings or base64 data
                const resolveFileBlob = async (fileSource) => {
                    if (fileSource.startsWith("blob:")) {
                        const res = await fetch(fileSource);
                        return await res.blob();
                    } else if (fileSource.startsWith("data:")) {
                        return dataURLtoBlob(fileSource);
                    }
                    return null;
                };

                // 1. Process Main Profile Photo
                if (globalFormData.mainPhoto) {
                    const isLocal = globalFormData.mainPhoto.startsWith("blob:") || globalFormData.mainPhoto.startsWith("data:");
                    if (isLocal) {
                        try {
                            const fileBlob = await resolveFileBlob(globalFormData.mainPhoto);
                            if (fileBlob) {
                                const ext = getExtensionFromMime(fileBlob.type);
                                const filename = `profile_main_photo.${ext}`;
                                stepPayload.append("main_photo", new File([fileBlob], filename, { type: fileBlob.type }), filename);
                            }
                        } catch (err) {
                            console.error("Failed to parse main photo layout stream:", err);
                        }
                    }
                }

                // 2. Process Gallery Photos
                if (globalFormData.galleryPhotos && globalFormData.galleryPhotos.length > 0) {
                    const selectedPhotos = globalFormData.galleryPhotos.slice(0, 2);
                    for (let i = 0; i < selectedPhotos.length; i++) {
                        const photoItem = selectedPhotos[i];
                        const photoUrl = photoItem.url || photoItem.photo || photoItem;

                        if (photoUrl) {
                            const isLocal = photoUrl.startsWith("blob:") || photoUrl.startsWith("data:");
                            if (isLocal) {
                                try {
                                    const fileBlob = await resolveFileBlob(photoUrl);
                                    if (fileBlob) {
                                        const ext = getExtensionFromMime(fileBlob.type);
                                        const filename = `gallery_item_${i}.${ext}`;
                                        stepPayload.append("photos[]", new File([fileBlob], filename, { type: fileBlob.type }), filename);
                                    }
                                } catch (err) {
                                    console.error(`Step gallery photo mapping issue at index ${i}:`, err);
                                }
                            }
                        }
                    }
                }

                // 3. Process Video Profile File
                if (globalFormData.videoFile) {
                    const isLocal = globalFormData.videoFile.startsWith("blob:") || globalFormData.videoFile.startsWith("data:");
                    if (isLocal) {
                        try {
                            const fileBlob = await resolveFileBlob(globalFormData.videoFile);
                            if (fileBlob) {
                                const ext = getExtensionFromMime(fileBlob.type);
                                const filename = `profile_media_video.${ext}`;
                                stepPayload.append("main_video", new File([fileBlob], filename, { type: fileBlob.type }), filename);
                            }
                        } catch (err) {
                            console.error("Failed to parse step profile video stream:", err);
                        }
                    }
                }
                break;

            default:
                break;
        }

        try {
            const apiResponse = await postApiWithAuthFile("/create-profile-step-2", stepPayload);

            if (apiResponse && (apiResponse.success || apiResponse.code === 200)) {
                const responseData = apiResponse.data || apiResponse.profile_data || apiResponse;
                const extractedId = responseData?.id || responseData?.profile_id || apiResponse?.profile_id || responseData?.[0]?.id;

                if (extractedId) {
                    setProfileId(extractedId);

                    const currentProfileStr = localStorage.getItem("profile_data");
                    let currentProfileArr = [{}];
                    if (currentProfileStr) {
                        try {
                            const parsed = JSON.parse(currentProfileStr);
                            currentProfileArr = Array.isArray(parsed) ? parsed : [parsed];
                        } catch (e) {
                            currentProfileArr = [{}];
                        }
                    }
                    currentProfileArr[0] = { ...currentProfileArr[0], ...responseData, id: extractedId };
                    localStorage.setItem("profile_data", JSON.stringify(currentProfileArr));
                }

                dispatch(clearErrors());
                dispatch(setStep(currentStep + 1));
            } else {
                alert(apiResponse?.message || `Failed to save Step ${currentStep} progress values.`);
            }
        } catch (error) {
            console.error(`Step ${currentStep} network processing failure trace:`, error);
            alert(error?.response?.data?.message || "Communication failure encountered processing step values.");
        } finally {
            setSubmitting(false);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            dispatch(clearErrors());
            dispatch(setStep(currentStep - 1));
        }
    };

    const handleFinalSubmit = async () => {
        if (!runValidation(currentStep)) return;

        setSubmitting(true);
        const formDataPayload = new FormData();

        formDataPayload.append("step", "6");
        if (profileId) {
            formDataPayload.append("profile_id", String(profileId));
            formDataPayload.append("id", String(profileId));
        }

        formDataPayload.append("partner_expectations", globalFormData.expectations || "");
        formDataPayload.append("partner_expectations_description", globalFormData.expectationsDescription || "");
        formDataPayload.append("match_horoscope", globalFormData.matchHoroscope ? 1 : 0);
        formDataPayload.append("contact_name", globalFormData.contactName || "");
        formDataPayload.append("contact_number", globalFormData.contactNumber || "");
        formDataPayload.append("sec_contact_name", globalFormData.secContactName || "");
        formDataPayload.append("sec_contact_number", globalFormData.secContactNumber || "");
        formDataPayload.append("address", globalFormData.address || "");
        formDataPayload.append("new_location", globalFormData.address || "");
        formDataPayload.append("correspondence_address", globalFormData.correspondenceAddress || "");
        formDataPayload.append("social_facebook", globalFormData.facebook || "");
        formDataPayload.append("social_instagram", globalFormData.instagram || "");
        formDataPayload.append("social_linkedin", globalFormData.linkedin || "");
        formDataPayload.append("declaration_accepted", globalFormData.declarationAccepted ? 1 : 0);
        formDataPayload.append("primary_country_code", globalFormData.contactCountryCode || "91");
        formDataPayload.append("sec_country_code", globalFormData.secContactCountryCode || "91");
        formDataPayload.append("country", globalFormData.primaryCountryId || globalFormData.primaryCountry || "India");

        try {
            const apiResponse = await postApiWithAuthFile("/create-profile-step-2", formDataPayload);

            if (apiResponse && (apiResponse.success || apiResponse.code === 200)) {
                const profileRecord = apiResponse.data || apiResponse.profile_data || apiResponse;
                const profileArray = Array.isArray(profileRecord) ? profileRecord : [profileRecord];
                localStorage.setItem("profile_data", JSON.stringify(profileArray));

                localStorage.removeItem("profile_creation_draft");
                localStorage.removeItem("profile_creation_step");

                setIsSubmitted(true);
            } else {
                alert(apiResponse?.message || "Failed to finalize and save profile registration settings.");
            }
        } catch (error) {
            console.error("Form final step sync transaction failure log:", error);
            alert(error.response?.data?.message || "Failed to submit final profile details.");
        } finally {
            setSubmitting(false);
        }
    };

    const renderStepComponent = () => {
        switch (currentStep) {
            case 1: return <Step1BasicInfo masters={masters} />;
            case 2: return <Step2PersonalDetails masters={masters} />;
            case 3: return <Step3FamilyBackground masters={masters} />;
            case 4: return <Step4EducationProfession masters={masters} />;
            // Pass structural pipeline tracking toggles directly to media template elements
            case 5: return <Step5ProfileMedia setIsMediaProcessing={setIsMediaProcessing} />;
            case 6: return <Step6PreferencesContact />;
            default: return <Step1BasicInfo masters={masters} />;
        }
    };

    return (
        <div className="main_wrapper">
            <section className="mt-4">
                {isSubmitted && (
                    <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1100 }}>
                        <div className="toast show align-items-center text-white bg-success border-0 shadow-lg" role="alert" aria-live="assertive" aria-atomic="true" style={{ borderRadius: '8px' }}>
                            <div className="d-flex">
                                <div className="toast-body d-flex align-items-center gap-3 p-3">
                                    <i className="fa fa-check-circle fa-2x text-white"></i>
                                    <div>
                                        <h6 className="m-0 fw-bold">Thank You!</h6>
                                        <small className="opacity-90">Profile changes saved. Redirecting to dashboard...</small>
                                    </div>
                                    <div className="spinner-border spinner-border-sm text-light ms-2" role="status"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="container">
                    <div className="matrimony-card">
                        <div className="row g-0">
                            <div className="col-lg-5">
                                <div className="step-sidebar">
                                    <div className="leaf-top"><img src={`${process.env.PUBLIC_URL}/assets/imgs/8.png`} alt="" /></div>
                                    <div className="leaf-bottom"><img src={`${process.env.PUBLIC_URL}/assets/imgs/5.png`} alt="" /></div>
                                    {steps.map((step, idx) => (
                                        <div className="step-item" key={step.id}>
                                            <div className="step-left">
                                                <div className={`step-number ${currentStep === step.id ? "active" : ""} ${currentStep > step.id ? "completed" : ""}`}>{step.id}</div>
                                                {idx !== steps.length - 1 && <div className={`step-line ${currentStep > step.id ? "line-active" : ""}`}></div>}
                                            </div>
                                            <div className="step-content"><h5>{step.title}</h5><p>{step.sub}</p></div>
                                        </div>
                                    ))}
                                    <div className='log-bg'></div>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="form-section">
                                    <div className="log_re_heading">
                                        <h2>{isEditMode ? "Modify Your Matrimony Profile" : "Create Your Matrimony Profile"}</h2>
                                        <p>Share about yourself and find the right match.</p>
                                    </div>
                                    <div className="my-4">{renderStepComponent()}</div>
                                    <div className="d-flex gap-3 mt-4">
                                        {currentStep > 1 && (
                                            <button className="btn back_btn w-50" onClick={prevStep} disabled={submitting || isSubmitted || isMediaProcessing}>
                                                Previous
                                            </button>
                                        )}
                                        <button
                                            className="btn next-btn w-100"
                                            onClick={currentStep === steps.length ? handleFinalSubmit : nextStep}
                                            disabled={submitting || isSubmitted || isMediaProcessing}
                                        >
                                            {currentStep === steps.length ? (
                                                submitting ? "Submitting..." : (isEditMode ? "Save Changes" : "Submit Form")
                                            ) : (
                                                submitting ? "Saving Step..." : (isMediaProcessing ? "Processing Media..." : "Next Step")
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CreateProfile;